import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Box } from 'lucide-react';
import type { ResolvedLocation } from '../../services/sensorBoxLocations';
import {
  boxesAtLocation,
  createBox,
  isPublicBox,
  loadBoxes,
  toResolvedBox,
  type ApiBox,
  type ResolvedBox,
} from '../../services/sensorBoxBoxes';

type Mode = 'existing' | 'new';

type BoxStepProps = {
  location: ResolvedLocation;
  resolved: ResolvedBox | null;
  onResolved: (box: ResolvedBox) => void;
  onClear: () => void;
  onContinue?: () => void;
};

const inputClass =
  'w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1';
const inputErrorClass = 'border-red-300 focus-visible:ring-red-400';

export default function BoxStep({ location, resolved, onResolved, onClear, onContinue }: BoxStepProps) {
  const [mode, setMode] = useState<Mode>('existing');
  const [boxes, setBoxes] = useState<ApiBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await loadBoxes();
        if (!cancelled) setBoxes(data);
      } catch {
        if (!cancelled) {
          setLoadError('Could not load sensor boxes from the Seawall API.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const locationBoxes = useMemo(
    () => boxesAtLocation(boxes, location.id),
    [boxes, location.id],
  );

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setNicknameError(null);
  };

  const confirmExisting = () => {
    const selected = locationBoxes.find((box) => String(box.ID) === pickedId);
    if (!selected) {
      setFormError('Select an existing box at this location to continue.');
      return;
    }
    onResolved(toResolvedBox(selected, location.id, true));
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = nickname.trim();
    setNicknameError(null);
    setFormError(null);

    if (!trimmedName) {
      setNicknameError('A nickname is required for a new box.');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createBox({
        locationID: location.id,
        nickname: trimmedName,
        public: isPublic,
      });
      onResolved(created);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not create this box.');
    } finally {
      setSubmitting(false);
    }
  };

  if (resolved) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-teal-700 mb-1">Box selected</p>
            <h2 className="text-2xl text-[#004F56] mb-2">{resolved.nickname}</h2>
            <dl className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
              <div>
                <dt className="text-gray-500">Box ID</dt>
                <dd className="font-medium">{resolved.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Location ID</dt>
                <dd className="font-medium">{resolved.locationID}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Visibility</dt>
                <dd className="font-medium">{resolved.isPublic ? 'Public' : 'Private'}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-teal-700">
              {resolved.reused
                ? 'This existing box will be used for the sensor array.'
                : 'This new box ID will be used for the sensor array.'}
            </p>
            {onContinue && (
              <button
                type="button"
                onClick={onContinue}
                className="mt-6 w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md"
              >
                Continue to sensor array
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 text-sm font-medium text-teal-700 hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="grid grid-cols-2 border-b border-gray-100">
        <button
          type="button"
          onClick={() => switchMode('existing')}
          disabled={submitting}
          className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
            mode === 'existing'
              ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Existing box
        </button>
        <button
          type="button"
          onClick={() => switchMode('new')}
          disabled={submitting}
          className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
            mode === 'new'
              ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          New box
        </button>
      </div>

      <div className="p-6 sm:p-8">
        {loadError && (
          <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
            {loadError}
          </div>
        )}

        {mode === 'existing' ? (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Boxes already assigned to {location.siteName} (location ID {location.id}).
            </p>

            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading boxes…</p>
            ) : locationBoxes.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">
                No boxes are assigned to this location yet. Create a new box instead.
              </p>
            ) : (
              <ul className="space-y-2 max-h-80 overflow-y-auto">
                {locationBoxes.map((box) => {
                  const id = String(box.ID);
                  const selected = pickedId === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPickedId(id);
                          setFormError(null);
                        }}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                          selected
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/40'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Box className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-teal-600' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium text-gray-900">{box.nickname || `Box ${id}`}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              ID {id} · {isPublicBox(box) ? 'Public' : 'Private'}
                              {box.assembleDateTime
                                ? ` · Assembled ${new Date(box.assembleDateTime).toLocaleDateString()}`
                                : ''}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                {formError}
              </div>
            )}

            <button
              type="button"
              onClick={confirmExisting}
              disabled={!pickedId || loading || locationBoxes.length === 0}
              className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60"
            >
              Use this box
            </button>
          </div>
        ) : (
          <form onSubmit={handleCreate} noValidate className="space-y-5">
            <p className="text-gray-600 text-sm">
              Create a box at this location. The location ID from the previous step is assigned
              automatically.
            </p>

            <div>
              <label htmlFor="boxNickname" className="block text-sm font-medium text-gray-700 mb-1.5">
                Nickname
              </label>
              <input
                id="boxNickname"
                value={nickname}
                onChange={(event) => setNickname(event.target.value)}
                placeholder="box1"
                className={`${inputClass} ${nicknameError ? inputErrorClass : ''}`}
              />
              {nicknameError && <p className="mt-1.5 text-xs text-red-500">{nicknameError}</p>}
            </div>

            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(event) => setIsPublic(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Public box
            </label>

            {formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating box…' : 'Create box'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
